import { useState } from "react";
import { AlertCircle, ArrowRight, Check, Eye, EyeOff, Loader2, Send, ShieldCheck, Sparkles } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import {
  Hint,
  SideCard,
  Steps,
  SuccessBox,
  monoInputCls,
  type PanelProps,
} from "./channel-wizard-kit";

/* Официальный WhatsApp Business API (WABA): Phone number ID + постоянный токен.
   Вариант для рассылок по шаблонам и зелёной галочки. */

const DEMO_PHONE_ID = "109384756201947";
const DEMO_TOKEN = "EAAG9kZBvXkD0BO7ZC9f2LmQwR4tYuV5xN8pKjH3sD1aE6cF2gB0";

export default function WabaPanel({ onDone }: PanelProps) {
  const [phoneId, setPhoneId] = useState("");
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [check, setCheck] = useState<"idle" | "checking" | "ok" | "error">("idle");

  const ready = phoneId.replace(/\D/g, "").length >= 6 && token.trim().length >= 20;

  const verify = () => {
    setCheck("checking");
    setTimeout(() => setCheck(ready ? "ok" : "error"), 900);
  };

  const fillDemo = () => {
    setPhoneId(DEMO_PHONE_ID);
    setToken(DEMO_TOKEN);
    setCheck("idle");
  };

  const connect = () => {
    onDone("WABA · +7 916 402-18-73", [
      { label: t("Тип"), value: "WhatsApp Business API" },
      { label: t("Бизнес"), value: "Hub Market" },
      { label: t("Номер"), value: "+7 916 402-18-73" },
      { label: t("Рассылки"), value: t("шаблоны включены") },
      { label: t("Токен"), value: t("сохранён в защищённом виде") },
    ]);
  };

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={check === "ok" ? 3 : token.trim().length > 0 || phoneId.trim().length > 0 ? 2 : 1}
          items={[
            t("Откройте Meta Business Suite → аккаунт WhatsApp Business"),
            t("Скопируйте Phone number ID и постоянный токен доступа"),
            t("Проверьте соединение и подключите официальный номер"),
          ]}
        />

        <div className="mt-4">
          <label
            htmlFor="waba-phone-id"
            className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
          >
            Phone number ID
          </label>
          <input
            id="waba-phone-id"
            value={phoneId}
            onChange={(e) => {
              setPhoneId(e.target.value.replace(/\D/g, "").slice(0, 20));
              setCheck("idle");
            }}
            placeholder="109384756201947"
            inputMode="numeric"
            autoComplete="off"
            className={cn(monoInputCls, "mt-1.5")}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t("Числовой ID номера из раздела «Настройки API» в Meta Business.")}
          </p>
        </div>

        <div className="mt-3">
          <label
            htmlFor="waba-token"
            className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
          >
            {t("Постоянный токен доступа")}
          </label>
          <div className="relative mt-1.5">
            <input
              id="waba-token"
              type={showToken ? "text" : "password"}
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setCheck("idle");
              }}
              placeholder="EAAG…"
              autoComplete="off"
              spellCheck={false}
              className={cn(monoInputCls, "pe-10")}
            />
            <button
              type="button"
              onClick={() => setShowToken((v) => !v)}
              aria-label={showToken ? t("Скрыть токен") : t("Показать токен")}
              className="absolute end-1.5 top-1/2 grid size-7 -translate-y-1/2 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              {showToken ? <EyeOff className="size-3.5" aria-hidden /> : <Eye className="size-3.5" aria-hidden />}
            </button>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fillDemo}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-primary transition hover:brightness-110"
          >
            <Sparkles className="size-3" aria-hidden />
            {t("Заполнить демо-данными")}
          </button>
          <span className="text-[11px] text-muted-foreground">·</span>
          <button
            type="button"
            onClick={verify}
            disabled={!ready || check === "checking"}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-primary transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {check === "checking" ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {t("Проверить соединение")}
          </button>
        </div>

        {check === "error" ? (
          <p className="mt-2.5 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
            <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
            {t("Meta не приняла данные. Проверьте Phone number ID (только цифры) и токен — он длиной не меньше 20 символов.")}
          </p>
        ) : null}

        {check === "ok" ? (
          <SuccessBox title={t("Соединение установлено — Meta отвечает")}>
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-3 border-b border-emerald-600/20 py-1.5">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Бизнес")}</span>
                <span className="text-[12.5px] font-semibold">Hub Market</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-b border-emerald-600/20 py-1.5">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Номер")}</span>
                <span className="text-[12.5px] font-semibold">+7 916 402-18-73</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-b border-emerald-600/20 py-1.5">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Качество")}</span>
                <span className="text-[12.5px] font-semibold">{t("зелёное · высокая репутация")}</span>
              </div>
              <div className="flex items-center justify-between gap-3 py-1.5">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Шаблоны")}</span>
                <span className="text-[12.5px] font-semibold">{t("12 согласовано")}</span>
              </div>
            </div>
          </SuccessBox>
        ) : null}

        <button
          type="button"
          onClick={connect}
          disabled={check !== "ok"}
          className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("Подключить официальный номер")}
          <ArrowRight className="size-3.5" aria-hidden />
        </button>

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Токен видит только администратор и хранится в защищённом виде. Продавцы получают диалоги без доступов к API.")}
        </Hint>
      </div>

      {/* правая колонка */}
      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <SideCard title={t("Официальный аккаунт")}>
          <div className="mt-2.5 flex items-center gap-2.5">
            <span
              className="grid size-10 flex-none place-items-center rounded-full bg-primary/10 text-lg leading-none text-primary"
              aria-hidden
            >
              <i className="ri-whatsapp-line" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[12.5px] font-semibold">Hub Market</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {check === "ok" ? "verified business" : t("ждём проверку данных")}
              </p>
            </div>
          </div>
          <div className="mt-3 rounded-btn bg-muted/70 p-2.5">
            <p className="text-[11.5px] leading-relaxed text-foreground">
              {t("Здравствуйте! Ваш заказ №1042 собран — можно забирать.")}
            </p>
            <p className="mt-1 text-[10px] text-muted-foreground">{t("так выглядит сообщение по шаблону")}</p>
          </div>
        </SideCard>

        <SideCard title={t("Что даёт WABA")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              t("Рассылки по согласованным шаблонам"),
              t("Зелёная галочка и имя бренда"),
              t("Несколько продавцов на одном номере"),
              t("Отчёты о доставке и прочтении"),
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground"
              >
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>

        <div className="rounded-card border border-border bg-background p-3">
          <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
            <Send className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
            {t("Нужен просто номер компании без рассылок? Выберите обычный WhatsApp — вход по QR-коду или по номеру телефона.")}
          </p>
        </div>
      </div>
    </div>
  );
}